import { Box, useMediaQuery, useTheme } from '@mui/material';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { isEntityOrderableNow } from '../../catalog/services/CatalogService';
import AddButton from '../../Common/components/AddButton';
import EntityUnavailableText from '../../Common/components/EntityUnavailableText';
import { AddButtonRedirect } from '../../Common/models/AddButtonRedirect';
import { Product, Sku } from '../../my-lemonade-library/model/Catalog';
import { OrderOption } from '../../my-lemonade-library/model/Order';
import { SkuExtended } from '../../my-lemonade-library/model/ProductExtended/ProductExtended';
import { CatalogEntityType } from '../../my-lemonade-library/src/catalogs/models/CatalogEntity';
import PlusMinusQuantityButton from '../../orders/components/PlusMinusQuantityButton';
import { RootState, useTypedSelector } from '../../redux/root-reducer';

interface AddProductButtonProps {
    onAddProduct: (redirect: AddButtonRedirect) => void;
    buttonTitleId?: string;
    hideQuantityButton?: boolean;
    price: string;
    quantity: number;
    onAdd: () => void;
    onRemove: () => void;
    hidePrice?: boolean;
    isButtonDisabled: boolean;
    product: Product;
    addToDeal: boolean;  // Tells if the product is being added to a deal
    displayFastCheckoutButton: boolean;
    selectedSku: SkuExtended;
    selectedOptions: OrderOption[];
}

const AddProductButton: React.FC<AddProductButtonProps> = (props) => {
    const {
        hideQuantityButton,
        onAddProduct,
        buttonTitleId,
        price,
        quantity,
        onAdd,
        onRemove,
        hidePrice,
        isButtonDisabled,
        product,
        addToDeal,
        displayFastCheckoutButton,
        selectedSku,
        selectedOptions
    } = props;

    const { order } = useTypedSelector((state: RootState) => state.order);
    const {
        selectedCatalog,
        selectedLocation,
        selectedTable
    } = useTypedSelector((state: RootState) => state.locations);

    const theme = useTheme();

    const isEntityOrderableNowResult = isEntityOrderableNow(selectedCatalog, selectedLocation, selectedTable, product);

    // Those values will change the buttons so that they fit for small screens
    const S_BREAKPOINT: number = 390;

    const isSRender = useMediaQuery(theme.breakpoints.up(S_BREAKPOINT));

    const maximumQuantity = useMemo(() => {
        const catalogItem: Sku | Product = !isNil(selectedSku?.remaining_qty) ? selectedSku : product;

        // Filter selected options
        const catalogOption = selectedCatalog?.data.options.filter((co) =>
            selectedOptions.some((so) => so.ref === co.ref)
        ) || [];

        // Calculate the lowest remaining_qty from options (only if they have a defined value)
        let lowestRemainingQty: number | undefined;
        if (catalogOption.length > 0) {
            const optionsWithStock = catalogOption.filter(option => !isNil(option.remaining_qty));
            if (optionsWithStock.length > 0) {
                lowestRemainingQty = optionsWithStock.reduce((min, option) =>
                    Math.min(min, option.remaining_qty!), optionsWithStock[0].remaining_qty!
                );
            }
        }

        // Determine the remaining_qty of the main entity (SKU or Product)
        let remainingQtyForEntity: number | undefined;

        if (catalogItem.entity_type === CatalogEntityType.SKU && !isNil((catalogItem as Sku).remaining_qty)) {
            remainingQtyForEntity = (catalogItem as Sku).remaining_qty;
        } else if (product && product.skus.length === 1 && !isNil(product.skus[0].remaining_qty)) {
            remainingQtyForEntity = product.skus[0].remaining_qty;
        }

        // Find existing order item for this product/sku
        const existingOrderItem = order.items?.find(orderItem => {
            if (!orderItem.update_id) {
                if (catalogItem.entity_type === CatalogEntityType.SKU) {
                    return orderItem.sku_ref === (catalogItem as Sku).ref;
                } else {
                    return orderItem.sku_ref === product.skus[0].ref;
                }
            }
        });

        // Determine maximum available quantity
        let maxAvailable: number | undefined;

        // If neither options nor entity have defined stock, no stock management
        if (isNil(lowestRemainingQty) && isNil(remainingQtyForEntity)) {
            return undefined;
        }

        // Take the smallest value between options and entity (only those defined)
        if (!isNil(lowestRemainingQty) && !isNil(remainingQtyForEntity)) {
            maxAvailable = Math.min(lowestRemainingQty, remainingQtyForEntity);
        } else if (!isNil(lowestRemainingQty)) {
            maxAvailable = lowestRemainingQty;
        } else if (!isNil(remainingQtyForEntity)) {
            maxAvailable = remainingQtyForEntity;
        }

        // Subtract quantity already ordered
        if (existingOrderItem && !isNil(maxAvailable)) {
            const availableQuantity = maxAvailable - existingOrderItem.quantity;
            return Math.max(0, availableQuantity);
        }

        return maxAvailable;
    }, [order, product, selectedSku, selectedOptions, selectedCatalog]);

    // Only check availability if added as a product and not in a deal
    if ((!addToDeal && !isEntityOrderableNowResult) || maximumQuantity === 0) {
        return (
            <EntityUnavailableText
                serviceType={order.service_type}
                entity={product}
            />
        );
    }

    return (

        <Box
            display="flex"
            flexDirection='column'
            justifyContent='space-around'
            width={1}
        >

            {!hideQuantityButton &&
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mb={3}
                >
                    <PlusMinusQuantityButton
                        quantity={quantity}
                        maximumValue={maximumQuantity}
                        onAdd={onAdd}
                        onMinus={onRemove}
                        isSRender={isSRender}
                    />
                </Box>
            }
            <AddButton
                onSubmit={onAddProduct}
                price={price}
                isButtonDisabled={maximumQuantity === 0 || isButtonDisabled}
                hidePrice={hidePrice}
                displayFastCheckoutButton={displayFastCheckoutButton}
                buttonTitleId={buttonTitleId}
            />
        </Box>
    );
}

export default AddProductButton;